import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  IconSearch,
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconTrash,
  IconX,
  IconDotsVertical,
  IconEye,
} from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import AddEmployeeModal from "./AddEmployeeModal";
import { useNavigate } from "react-router-dom";
import {
  fetchHrEmployees,
  deleteHrEmployee,
  updateHrEmployee,
} from "../../../apis/hrApi";
import toast from "react-hot-toast";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import EntriesSelector from "../../../components/common/EntriesSelector";
import RoleToggle from "../../../components/RoleToggle";

export default function Employee({
  activeSubTab = "Employee",
  setActiveSubTab = () => {},
  hrSubTabs = ["Employee", "Attendance", "Leaves", "Department", "Designation", "Holiday", "Shift Roster", "Appreciation", "Action", "Loan and Advances", "Organisational Chart"]
}: any) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [limit, setLimit] = useState(10);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const navigate = useNavigate();

  const [employees, setEmployees] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [columnsData, setColumnsData] = useState([
    { text: "Employee ID", value: "empId", isShow: true },
    { text: "Name", value: "name", isShow: true },
    { text: "Email", value: "email", isShow: true },
    { text: "Phone", value: "phone", isShow: true },
    { text: "Designation", value: "designation", isShow: true },
    { text: "Joining Date", value: "joiningDate", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col
      )
    );
  };

  const loadEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchHrEmployees();
      if (response.success && response.data?.employees) {
        const mapped = response.data.employees.map((emp: any) => ({
          id: emp.id || emp._id,
          empId: emp.employeeId || emp.empId || "-",
          name: emp.firstName
            ? `${emp.firstName} ${emp.lastName || ""}`
            : emp.name || "Unknown",
          email: emp.workEmail || emp.personalEmail || emp.email || "-",
          phone: emp.phoneNumber || emp.phone || "-",
          designation: emp.designationName || emp.designation || "-",
          joiningDate: emp.joiningDate
            ? new Date(emp.joiningDate).toLocaleDateString()
            : "-",
          status: emp.status || "ACTIVE",
          raw: emp,
        }));
        setEmployees(mapped);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load employees");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const nextStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      const res = await updateHrEmployee(id, { status: nextStatus });
      if (res.success) {
        toast.success(`Employee marked as ${nextStatus.toLowerCase()}`);
        loadEmployees();
      } else {
        toast.error("Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update employee status");
    }
  };

  const handleDeleteEmployee = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;

    try {
      const res = await deleteHrEmployee(id);
      if (res.success || res.statusCode === 200) {
        toast.success("Employee deleted successfully");
        loadEmployees();
      } else {
        toast.error("Failed to delete employee");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete employee");
    }
  };

  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return employees;
    const query = searchQuery.toLowerCase();
    return employees.filter((emp: any) =>
      [emp.empId, emp.name, emp.email, emp.designation]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [employees, searchQuery]);

  const paginatedEmployees = useMemo(() => {
    return filteredEmployees.slice(0, limit);
  }, [filteredEmployees, limit]);

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        label: (
          <div className="flex items-center justify-center">
            <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
              <input
                type="checkbox"
                className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
              />
              <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
                ✓
              </span>
            </label>
          </div>
        ),
        render: () => (
          <div className="flex items-center justify-center">
            <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
              <input
                type="checkbox"
                className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
              />
              <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
                ✓
              </span>
            </label>
          </div>
        ),
      }
    ];

    columnsData.forEach((col) => {
      if (!col.isShow) return;

      switch (col.value) {
        case "empId":
          cols.push({
            label: "EMPLOYEE ID",
            render: (s: any) => (
              <span className="font-semibold text-xs text-[#212121]">{s.empId}</span>
            ),
          });
          break;
        case "name":
          cols.push({
            label: "NAME",
            render: (s: any) => (
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-[#FBD037]/20 border border-[#FBD037]/40 flex items-center justify-center text-[10px] font-bold flex-shrink-0 text-[#212121]">
                  {s.name.charAt(0)}
                </div>
                <span className="font-semibold text-xs text-[#212121]">
                  {s.name}
                </span>
              </div>
            ),
          });
          break;
        case "email":
          cols.push({
            label: "EMAIL",
            render: (s: any) => <span className="text-xs text-gray-600">{s.email}</span>,
          });
          break;
        case "phone":
          cols.push({
            label: "PHONE",
            render: (s: any) => <span className="text-xs text-gray-600">{s.phone}</span>,
          });
          break;
        case "designation":
          cols.push({
            label: "DESIGNATION",
            render: (s: any) => (
              <span className="text-xs text-gray-600">{s.designation}</span>
            ),
          });
          break;
        case "joiningDate":
          cols.push({
            label: "JOINING DATE",
            render: (s: any) => (
              <span className="text-xs text-gray-600">{s.joiningDate}</span>
            ),
          });
          break;
        case "status":
          cols.push({
            label: "STATUS",
            render: (s: any) => {
              const on = s.status === "ACTIVE";
              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleStatus(s.id, s.status);
                  }}
                  className={`relative inline-flex w-10 h-5 rounded-full transition-colors ${
                    on ? "bg-[#212121]" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      on ? "translate-x-5" : ""
                    }`}
                  />
                </button>
              );
            },
          });
          break;
        case "actions":
          cols.push({
            label: "VIEW",
            render: (s: any) => (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/hr/employee/${s.id}`);
                }}
                className="p-1.5 border border-[#131327]/10 rounded-full bg-[#F7F6F2] hover:bg-[#e8e7e3] text-gray-600 hover:text-[#212121] transition-colors"
              >
                <IconEye size={13} stroke={2.5} />
              </button>
            ),
          });
          break;
      }
    });

    return cols;
  }, [columnsData, handleToggleStatus, handleDeleteEmployee, openDropdownId]);

  const handleRowClick = useCallback((emp: any) => {
    navigate(`/hr/employee/${emp.id}`);
  }, [navigate]);

  return (
    <div className="w-full mx-auto mt-0">
      <div className="grid grid-cols-12 gap-6 w-full">
        {/* Left Column: Employee List Table Card */}
        <div
          className={`${
            isAddModalOpen
              ? "col-span-12 xl:col-span-7"
              : "col-span-12"
          } flex flex-col gap-3 transition-all duration-300`}
        >
          <div className="bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl rounded-b-3xl rounded-tr-3xl border border-white/20 shadow-sm overflow-visible mb-6" style={{ minHeight: "150px" }}>
            {/* Header Row (Filters + Actions Unified) */}
            <div className="px-6 py-1.5 flex flex-col lg:flex-row lg:items-center justify-end gap-4 border-b border-slate-50">
              {/* Right side: Actions & Controls */}
              <div className="flex items-center flex-wrap gap-2.5">
                {/* Sliding Search */}
                <div className="relative flex items-center group">
                  <div
                    onClick={() => !showSearch && setShowSearch(true)}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch ? "w-64 px-2 cursor-default" : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                        if (showSearch) {
                          setSearchQuery("");
                        }
                      }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0 cursor-pointer"
                    >
                      <IconSearch size={16} />
                    </button>
                    {showSearch && (
                      <>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search..."
                        autoFocus
                        className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none focus:outline-none focus:ring-0"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchQuery("");
                          setShowSearch(false);
                        }}
                        className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0 cursor-pointer"
                      >
                        <IconX size={14} stroke={2.5} />
                      </button>
                      </>
                    )}
                  </div>
                  {!showSearch && (
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                      Search
                    </div>
                  )}
                </div>

                {/* Entries Selector */}
                <EntriesSelector
                  value={limit}
                  options={[10, 20, 50, 100]}
                  onChange={(val) => {
                    setLimit(val);
                  }}
                />

                {/* Columns Filter */}
                <ColumnsFilter
                  triggerClassName="border border-slate-200 bg-white rounded-lg p-1.5 cursor-pointer text-[#212121] hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 h-8 w-8 flex items-center justify-center shadow-sm select-none"
                  columnsData={columnsData}
                  setHeadings={setHeadings}
                />

                {/* Add Employee */}
                <div className="relative group">
                  <button
                    onClick={() => {
                      setIsAddModalOpen(true);
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#FBD037] bg-[#FBD037] text-[#131327] hover:bg-[#e2bd30] transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  >
                    <IconPlus stroke={1.5} size={18} />
                  </button>
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                    Add Employee
                  </div>
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="border-t border-slate-100 rounded-b-3xl overflow-hidden bg-white">
              {isLoading ? (
                <div className="p-8 flex justify-center items-center">
                  <svg
                    className="animate-spin h-6 w-6 text-[#FBD037]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                </div>
              ) : error ? (
                <div className="p-8 text-center text-red-500">{error}</div>
              ) : (
                <ListView
                  columns={columns}
                  data={paginatedEmployees}
                  trClassName="hover:bg-[#FBD037]/10 group transition-colors duration-150 border-b border-gray-100 cursor-pointer"
                  mainDivClassName="bg-[#F7F6F2] rounded-3xl"
                  mailDivClassName="custom-scrollbar bg-[#FCFDF8] rounded-3xl shadow-inner max-h-[75vh] min-h-[250px]"
                  scrollbarClassName="custom-scrollbar"
                  thClassName="text-[11px] font-semibold tracking-wider uppercase select-none text-[#131327]/70"
                  onRowClick={handleRowClick}
                />
              )}
            </div>
          </div>

          <div className="py-4 flex justify-center">
            <div className="flex items-center gap-1">
              <button
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
                disabled
              >
                <IconChevronLeft size={14} stroke={2.5} />
              </button>
              <button className="w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold bg-[#FBD037] border-[#FBD037] text-[#131327] transition shadow-sm">
                1
              </button>
              <button className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition shadow-sm">
                <IconChevronRight size={14} stroke={2.5} />
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Sidebar drawers */}
        {isAddModalOpen && (
          <div className="col-span-12 xl:col-span-5 flex animate-in fade-in slide-in-from-right-5 duration-300">
            <AddEmployeeModal
              isOpen={isAddModalOpen}
              onClose={() => setIsAddModalOpen(false)}
              onSuccess={loadEmployees}
            />
          </div>
        )}

      </div>
    </div>
  );
}
